import { Box, Stack, TooltipProps } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import Typography, { TypographyProps } from "@mui/material/Typography";
import BaseTooltip from "@ui/atoms/BaseTooltip/BaseTooltip";
import { FONT_WEIGHT, FontWeightToken } from "@themes/tokens/shared";
import React, { forwardRef } from "react";
import Icon from "@ui/atoms/Icon/Icon";

export type TooltipPlacementType = TooltipProps["placement"];

export interface BaseTypographyProps extends TypographyProps {
  loading?: boolean;
  text?: React.ReactNode;
  dataTestId?: string;
  tooltipText?: React.ReactNode;
  tooltipPlacement?: TooltipPlacementType;
  suffix?: React.ReactNode;
  skeletonWidth?: number;
  skeletonHeight?: number;
  /** One of the app's four font-weight tokens. Omit to use the variant's own weight. */
  weight?: FontWeightToken;
  children?: React.ReactNode;
  tooltipOnText?: boolean; // When true, shows tooltip on text instead of icon
}

const suffixStyle: React.CSSProperties = {
  color: "var(--mui-palette-text-secondary)",
  // Suffix is a secondary/quieter annotation next to the main text, so it
  // leans toward caption rather than body2. No theme access in this plain
  // CSSProperties object — 12px matches theme.typography.caption.fontSize.
  fontSize: "12px",
  fontWeight: 500,
  lineHeight: "20px",
  marginLeft: "4px"
};

/**
 * Merges the `weight` token with the caller-supplied `sx` into a single MUI
 * sx array. Pure — exported for tests.
 */
export const buildWeightSx = (
  weight: FontWeightToken | undefined,
  sx: TypographyProps["sx"]
) => [
  weight ? { fontWeight: FONT_WEIGHT[weight] } : {},
  ...(Array.isArray(sx) ? sx : [sx])
];

// Overrides only the two entries MUI gets wrong for our usage; every other
// variant keeps its default element.
const SEMANTIC_VARIANT_MAPPING = { subtitle1: "p", subtitle2: "p" } as const;

const BaseTypography = forwardRef<HTMLDivElement, BaseTypographyProps>(
  (
    {
      children,
      dataTestId,
      loading = false,
      skeletonHeight = 20,
      skeletonWidth = 150,
      suffix,
      text,
      tooltipOnText = false,
      tooltipPlacement = "top",
      tooltipText,
      // body2 (13px) is the app's dominant variant by a wide margin — a
      // caller with no opinion should land there, not on MUI's stock body1.
      variant = "body2",
      weight,
      ...typographyProps
    },
    ref
  ) => {
    const { sx, ...restTypographyProps } = typographyProps;

    const renderTypography = () => (
      <Typography
        ref={ref}
        variant={variant}
        // MUI maps subtitle1/subtitle2 to <h6>, so every subtitle in the app was
        // being announced as a heading — and as the deepest one, which broke
        // heading order wherever a subtitle sat near a real h1/h2. Subtitles are
        // not headings; render them as <p>. Purely semantic — Typography styles
        // off `variant`, not the element, so nothing moves visually.
        variantMapping={SEMANTIC_VARIANT_MAPPING}
        data-test-id={dataTestId}
        sx={buildWeightSx(weight, sx)}
        {...restTypographyProps}
      >
        {text || children}
        {suffix && <span style={suffixStyle}>{suffix}</span>}
      </Typography>
    );

    return loading ? (
      <Skeleton width={skeletonWidth} height={skeletonHeight} />
    ) : (
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          // Never wider than the parent: without this the wrapper sizes to its
          // text content and overflows, so an ellipsis passed via `sx` measures
          // 100% of the overflowing wrapper and never fires.
          maxWidth: "100%",
          minWidth: 0
        }}
      >
        {tooltipText && tooltipOnText ? (
          <BaseTooltip title={tooltipText} placement={tooltipPlacement}>
            <Box
              sx={{
                cursor: "pointer",
                display: "block",
                minWidth: 0,
                width: "100%"
              }}
            >
              {renderTypography()}
            </Box>
          </BaseTooltip>
        ) : (
          renderTypography()
        )}

        {tooltipText && !tooltipOnText ? (
          <BaseTooltip title={tooltipText} placement={tooltipPlacement}>
            <Stack
              data-test-id={
                dataTestId ? `${dataTestId}-tooltip-icon` : "tooltip-icon"
              }
              direction="row"
              // The icon carries no text, so it has to name itself — BaseTooltip
              // only describes. tabIndex makes it a real stop: MUI opens the
              // tooltip on focus, so the hint is reachable without a mouse.
              role="button"
              tabIndex={0}
              aria-label={
                typeof tooltipText === "string"
                  ? tooltipText
                  : "More information"
              }
              sx={{
                cursor: "pointer",
                ml: 1
              }}
            >
              <Icon name="info" tone="muted" />
            </Stack>
          </BaseTooltip>
        ) : null}
      </Stack>
    );
  }
);

BaseTypography.displayName = "BaseTypography";

export default BaseTypography;
